import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

import { DayNavigator } from "@/components/admin/DayNavigator";
import { addDaysIso, todayIso } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { SnapshotTable } from "@/components/data/SnapshotTable";
import { usePermissions } from "@/hooks/usePermissions";
import { errorMessage } from "@/lib/errors";
import { getDayNote } from "@/services/api/planner";
import { listDaySnapshots, updateSnapshot } from "@/services/api/snapshots";
import type { LoadSnapshot } from "@/services/api/types";

const DAYS = 4; // base day plus the following three days

/** Warehouse loadboard running off the published day snapshots. */
export function LoadboardPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const queryClient = useQueryClient();
  const canDispatch = has("loadboard.dispatched", "loads.write");
  const [baseDay, setBaseDay] = useState(todayIso());

  const days = useMemo(() => {
    return Array.from({ length: DAYS }, (_, index) => addDaysIso(baseDay, index));
  }, [baseDay]);

  const snapshotQueries = useQueries({
    queries: days.map((day) => ({
      queryKey: ["snapshots", day],
      queryFn: () => listDaySnapshots(day),
    })),
  });
  const byDay = useMemo(() => {
    const map = new Map<string, LoadSnapshot[]>();
    days.forEach((day, index) => {
      map.set(day, snapshotQueries[index]?.data ?? []);
    });
    return map;
  }, [days, snapshotQueries]);
  const isLoading = snapshotQueries.some((query) => query.isLoading);
  const anyPublished = useMemo(
    () => days.some((day) => (byDay.get(day)?.length ?? 0) > 0),
    [days, byDay],
  );

  // Per-day schedule notes written on the load planner, shown read-only here.
  const noteQueries = useQueries({
    queries: days.map((day) => ({
      queryKey: ["day-note", day],
      queryFn: () => getDayNote(day),
    })),
  });
  const notesByDay = useMemo(() => {
    const map = new Map<string, string | null>();
    days.forEach((day, index) => {
      map.set(day, noteQueries[index]?.data?.note ?? null);
    });
    return map;
  }, [days, noteQueries]);

  const markDispatched = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof updateSnapshot>[1] }) =>
      updateSnapshot(id, data),
    onSuccess: () => {
      toast.success(t("loadboard.dispatched"));
      for (const day of days) {
        void queryClient.invalidateQueries({ queryKey: ["snapshots", day] });
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const toggleDispatched = (row: LoadSnapshot) => {
    if (!canDispatch || row.status !== "booked") return;
    // The loadboard only moves a booked snapshot load to shipped (dispatched),
    // which also updates the live load; the dashboard admin then completes it.
    markDispatched.mutate({ id: row.id, data: { status: "dispatched" } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("loadboard.title")}</h1>
      </div>
      <DayNavigator day={baseDay} onChange={setBaseDay} />

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">{t("common.loading")}</CardContent>
        </Card>
      ) : !anyPublished ? (
        <EmptyState title={t("loadboard.noPublished")} />
      ) : (
        days.map((day) => (
          <section key={day} className="space-y-2">
            <div className="flex items-center justify-between border-b pb-1">
              <h2 className="text-lg font-semibold">{format(new Date(`${day}T00:00:00`), "EEEE d MMMM")}</h2>
            </div>
            {notesByDay.get(day) ? (
              <div className="rounded-md border border-muted bg-muted/40 px-3 py-2 text-sm">
                <span className="font-semibold">{t("loadboard.dayNote")}: </span>
                <span className="whitespace-pre-wrap">{notesByDay.get(day)}</span>
              </div>
            ) : null}
            <SnapshotTable
              rows={byDay.get(day) ?? []}
              emptyLabel={t("loadboard.noLoads")}
              renderTrailing={(load) => (
                <Button
                  variant={load.status === "booked" ? "default" : "outline"}
                  size="sm"
                  disabled={!canDispatch || load.status !== "booked"}
                  onClick={() => toggleDispatched(load)}
                >
                  {t("loadboard.dispatched")}
                </Button>
              )}
            />
          </section>
        ))
      )}
    </div>
  );
}
